import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  TextField,
  Typography,
  FormControl,
  FormLabel,
  Tooltip,
  IconButton,
} from "@mui/material";
import FmdBadIcon from "@mui/icons-material/FmdBad";

//パス
import { ERROR } from "../../routes/path";
//共通コンポーネント
import {
  MainColorButton,
  ToggleButtons,
  Pulldown,
  DateSelector,
} from "../../components";
import { getAddressData } from "../../utils/addressUtil";
import { closeKeyboard } from "../../utils/closeKeyboard";
// 定数
import { PREFECTURE_ARRAY } from "../../common/constants";
// API
import { getSystemConstants } from "../../server/axios";
//redux
import store from "../../store";
import { setSystemConstants } from "../../store/actions/prescription";

const regex = /^[0-9\b]+$/;

/**
 * 利用者情報登録画面
 * @returns
 */
const BaseInfoArea = ({ setLoadingOpen, setOpenSnackbar, data, setData }) => {
  const navigate = useNavigate();
  const endY = useRef(0);
  //性別ToggleButtons処理
  const handleAlignment = (_, newAlignment) => {
    setData({ ...data, gender: newAlignment });
  };

  //性別
  const [genderArray, setGenderArray] = useState([]);
  //性別helper表示非表示設定
  const [genderOpen, setGenderOpen] = useState(false);
  const [genderClosed, setGenderClosed] = useState(false);

  //減免フラグToggleButtons処理
  const handleExemptionFlg = (_, exemption) => {
    setData({ ...data, exemptionFlg: exemption === "1" });
  };
  //減免フラグ
  const [exemptionArray] = useState([
    { id: "1", label: "あり" },
    { id: "2", label: "なし" },
  ]);
  useEffect(() => {
    // 性別の選択肢を取得
    const selectSystemConstants = (state) => state.prescription.systemConstants;
    const systemConstants = selectSystemConstants(store.getState());
    if (systemConstants) {
      setGenderArray(
        systemConstants
          .filter((r) => r.ConstCd === "sex_class" && r.StringSub1 !== "none")
          .map((s) => ({
            id: s.ConstBranchCd,
            label: s.ConstString,
          }))
      );
    } else {
      setLoadingOpen(true);
      //性別選択肢取得と設定
      getSystemConstants({ isPharmacy: true }).then((res) => {
        if (res.data) {
          store.dispatch(setSystemConstants(res.data));
          setGenderArray(
            res.data
              .filter(
                (r) => r.ConstCd === "sex_class" && r.StringSub1 !== "none"
              )
              .map((s) => ({
                id: s.ConstBranchCd,
                label: s.ConstString,
              }))
          );
        } else {
          navigate(ERROR);
        }
        setLoadingOpen(false);
      });
    }
  }, []);

  //郵便番号で住所検索
  const searchAddress = async () => {
    setLoadingOpen(true);
    const address = await getAddressData(data.postalCode);
    if (address && address.length > 0) {
      const prefectureResult =
        PREFECTURE_ARRAY.find((p) => p === address[0].address1) || "";
      setData({
        ...data,
        prefecture: prefectureResult || "",
        municipalities: address[0].address2 || "",
        townNameAddress: address[0].address3 || "",
      });
    } else {
      //郵便番号間違い処理
      setOpenSnackbar({
        status: true,
        message: "正しい郵便番号を入力してください。",
      });
    }
    setLoadingOpen(false);
  };

  return (
    <div
      onTouchStartCapture={() => {
        endY.current = 0;
      }}
      onTouchMoveCapture={(e) => {
        endY.current = e.touches[0].clientY;
      }}
      onTouchEndCapture={(e) => {
        if (endY.current !== 0) {
          e.stopPropagation();
        } else {
          if (e.target.id !== "tooltipAreaGender" && genderOpen === true) {
            setGenderOpen(false);
            setGenderClosed(true);
          }
        }
      }}
    >
      {genderArray && genderArray.length > 0 && (
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box width="19%">
              <Typography>名前</Typography>
            </Box>
            <Stack width="81%" direction="row" alignItems="center">
              <Typography color="red" width="15%">
                必須
              </Typography>
              <Stack
                width="85%"
                direction="row"
                alignItems="center"
                spacing={1}
              >
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="(姓)"
                  onChange={(event) => {
                    setData({ ...data, lastName: event.target.value });
                  }}
                  onKeyDown={(event) => closeKeyboard(event)}
                  value={data.lastName}
                />
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="(名)"
                  onChange={(event) => {
                    setData({ ...data, firstName: event.target.value });
                  }}
                  onKeyDown={(event) => closeKeyboard(event)}
                  value={data.firstName}
                />
              </Stack>
            </Stack>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box width="19%">
              <Typography>かな</Typography>
            </Box>
            <Stack width="81%" direction="row" alignItems="center">
              <Typography color="red" width="15%">
                必須
              </Typography>
              <Stack
                width="85%"
                direction="row"
                alignItems="center"
                spacing={1}
              >
                <TextField
                  width="60%"
                  variant="outlined"
                  size="small"
                  placeholder="(姓)"
                  onChange={(event) => {
                    setData({ ...data, lastNameKana: event.target.value });
                  }}
                  onKeyDown={(event) => closeKeyboard(event)}
                  value={data.lastNameKana}
                />
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="(名)"
                  onChange={(event) => {
                    setData({ ...data, firstNameKana: event.target.value });
                  }}
                  onKeyDown={(event) => closeKeyboard(event)}
                  value={data.firstNameKana}
                />
              </Stack>
            </Stack>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box width="19%">
              <Typography style={{ fontSize: "0.8rem" }}>戸籍の性別</Typography>
            </Box>
            <Box width="81%">
              <Stack direction="row" alignItems="center">
                <Typography color="red" width="15%">
                  必須
                </Typography>
                <Stack
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="center"
                >
                  <FormControl fullWidth>
                    <ToggleButtons
                      items={genderArray}
                      value={data.gender}
                      setValue={handleAlignment}
                    />
                  </FormControl>
                  <Tooltip
                    open={genderOpen}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    PopperProps={{
                      disablePortal: true,
                      sx: { maxWidth: "85vw" },
                    }}
                    title={
                      <Box id="tooltipAreaGender">
                        <Typography id="tooltipAreaGender">
                          －性別について－
                          <br />
                          薬機法上、戸籍に登録された性別の入力をお願いしております。
                          申し訳ございませんが、ジェンダーの方でも戸籍上の性別を入力してください。
                        </Typography>
                      </Box>
                    }
                  >
                    <IconButton
                      id="tooltipButtonGender"
                      onTouchEnd={() => {
                        if (!genderClosed) {
                          setGenderOpen(!genderOpen);
                        }
                        setGenderClosed(false);
                      }}
                    >
                      <FmdBadIcon id="tooltipButtonGender" color="error" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box width="19%">
              <Typography style={{ fontSize: "0.8rem" }}>
                戸籍の<span style={{ display: "block" }}>生年月日</span>
              </Typography>
            </Box>
            <Box width="81%">
              <Stack direction="row" alignItems="center">
                <Typography color="red" width="15%">
                  必須
                </Typography>
                <DateSelector
                  birthday={data.birthday}
                  setBirthday={(birthday) => setData({ ...data, birthday })}
                />
              </Stack>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box width="19%">
              <Typography>郵便番号</Typography>
            </Box>
            <Stack width="81%" direction="row" alignItems="center">
              <Typography color="red" width="15%">
                必須
              </Typography>
              <Stack
                width="85%"
                direction="row"
                alignItems="center"
                spacing={1}
              >
                <Box width="45%">
                  <TextField
                    type="text"
                    variant="outlined"
                    size="small"
                    onChange={(event) => {
                      if (
                        event.target.value === "" ||
                        regex.test(event.target.value)
                      ) {
                        setData({ ...data, postalCode: event.target.value });
                      }
                    }}
                    onKeyDown={(event) => closeKeyboard(event)}
                    value={data.postalCode}
                  />
                </Box>
                <MainColorButton
                  label="住所検索"
                  onClick={searchAddress}
                  style={{ height: "4vh" }}
                />
              </Stack>
            </Stack>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box width="32%" />
            <Box width="68%">
              <Typography>※ハイフン(-)なし</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box width="19%">
              <Typography>都道府県</Typography>
            </Box>
            <Box width="81%">
              <Stack direction="row" alignItems="center">
                <Typography color="red" width="15%">
                  必須
                </Typography>
                <Box width="45%">
                  <FormControl fullWidth>
                    <Pulldown
                      size="small"
                      withLabel={false}
                      withMenuProps={true}
                      items={PREFECTURE_ARRAY.map((p) => ({ id: p, value: p }))}
                      value={data.prefecture}
                      setValue={(p) => setData({ ...data, prefecture: p })}
                    />
                  </FormControl>
                </Box>
              </Stack>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box width="19%">
              <Typography>市区町村</Typography>
            </Box>
            <Box width="81%">
              <Stack direction="row" alignItems="center">
                <Typography color="red" width="15%">
                  必須
                </Typography>
                <Box width="83%">
                  <FormControl fullWidth>
                    <TextField
                      variant="outlined"
                      size="small"
                      onChange={(event) => {
                        setData({
                          ...data,
                          municipalities: event.target.value,
                        });
                      }}
                      onKeyDown={(event) => closeKeyboard(event)}
                      value={data.municipalities}
                    />
                  </FormControl>
                </Box>
              </Stack>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box width="19%">
              <Typography>町名番地</Typography>
            </Box>
            <Box width="81%">
              <Stack direction="row" alignItems="center">
                <Typography color="red" width="15%">
                  必須
                </Typography>
                <Box width="83%">
                  <FormControl fullWidth>
                    <TextField
                      variant="outlined"
                      size="small"
                      onChange={(event) => {
                        setData({
                          ...data,
                          townNameAddress: event.target.value,
                        });
                      }}
                      onKeyDown={(event) => closeKeyboard(event)}
                      value={data.townNameAddress}
                    />
                  </FormControl>
                </Box>
              </Stack>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box width="19%">
              <Typography>建物名等</Typography>
            </Box>
            <Box width="81%">
              <Stack direction="row" alignItems="center">
                <Box width="15%" />
                <Box width="83%">
                  <FormControl fullWidth>
                    <TextField
                      variant="outlined"
                      size="small"
                      onChange={(event) => {
                        setData({
                          ...data,
                          buildingNameEtc: event.target.value,
                        });
                      }}
                      onKeyDown={(event) => closeKeyboard(event)}
                      value={data.buildingNameEtc}
                    />
                  </FormControl>
                </Box>
              </Stack>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box width="19%">
              <Typography>携帯電話</Typography>
            </Box>
            <Box width="81%">
              <Stack direction="row" alignItems="center">
                <Typography color="red" width="15%">
                  必須
                </Typography>
                <Box width="83%">
                  <FormControl fullWidth>
                    <TextField
                      type="text"
                      variant="outlined"
                      size="small"
                      onChange={(event) => {
                        if (
                          event.target.value === "" ||
                          regex.test(event.target.value)
                        ) {
                          setData({ ...data, phoneNumber: event.target.value });
                        }
                      }}
                      value={data.phoneNumber}
                      onKeyDown={(event) => closeKeyboard(event)}
                    />
                  </FormControl>
                </Box>
              </Stack>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box width="32%" />
            <Box width="68%">
              <Typography>※ハイフン(-)なし</Typography>
            </Box>
          </Stack>
          <FormControl>
            <FormLabel>
              <Typography color="black" display="inline">
                自己負担の減免等を証明する書類
              </Typography>
              <Typography color="red" display="inline" marginLeft="5%">
                必須
              </Typography>
              <br />
              <Typography color="black" display="inline">
                （乳幼児医療証などの各種受給資格証）
              </Typography>
            </FormLabel>
            <Box sx={{ marginLeft: "35%" }}>
              <FormControl fullWidth>
                <ToggleButtons
                  items={exemptionArray}
                  value={!!data.exemptionFlg ? "1" : "2"}
                  setValue={handleExemptionFlg}
                />
              </FormControl>
            </Box>
          </FormControl>
        </Stack>
      )}
    </div>
  );
};

export default BaseInfoArea;
